import { useCallback, useEffect, useRef, useState } from "react";
import { API, apiFetch } from "../lib/api";
import { T, base } from "../lib/theme";
import { Btn, Card, Icon } from "../components";

const fileIcon  = (m="", c="#fff") => m.startsWith("image/")
  ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>
  : m==="application/pdf"
  ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
  : m.startsWith("video/")
  ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
  : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="1.8"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>;
const fileColor = (m="") => m.startsWith("image/") ? T.sky : m==="application/pdf" ? T.rose : m.startsWith("video/") ? T.violet : T.amber;
const fmtSize   = (b=0)  => b<1024 ? `${b}B` : b<1048576 ? `${(b/1024).toFixed(1)}KB` : `${(b/1048576).toFixed(1)}MB`;

const MediaThumb = ({ mr, token, color }) => {
  const [src, setSrc] = useState("");
  useEffect(() => {
    if (!mr.mimeType?.startsWith("image/") || !mr.filePath) return undefined;
    let active = true;
    let objectUrl = "";
    const filename = String(mr.filePath).split(/[\\/]/).pop();
    fetch(`${API}/api/media/${encodeURIComponent(filename)}`, { headers:{ Authorization:token } })
      .then(res => { if (!res.ok) throw new Error("Preview unavailable"); return res.blob(); })
      .then(blob => { if (active) { objectUrl = URL.createObjectURL(blob); setSrc(objectUrl); } })
      .catch(() => {});
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [mr.filePath, mr.mimeType, token]);

  return src
    ? <img src={src} alt={mr.name} style={{width:"100%",height:"100%",objectFit:"cover",borderRadius:10}}/>
    : fileIcon(mr.mimeType, color);
};

const LocalImagePreview = ({ file }) => {
  const [src, setSrc] = useState("");
  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setSrc(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  return src ? <img src={src} alt="" style={{width:"100%",height:"100%",objectFit:"cover"}}/> : null;
};

// ── Media reply card ───────────────────────────────────────────────
const MediaCard = ({ mr, onDelete, onEdit, token }) => {
  const [del,  setDel]  = useState(false);
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(mr.name);
  const c = fileColor(mr.mimeType);

  if (edit) return (
    <div style={{ padding:14, borderRadius:14, background:T.bg0, border:`1px solid ${T.emerald}44`, marginBottom:10 }}>
      <label style={{ display:"block", fontSize:11, color:T.text3, marginBottom:5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em" }}>File Name</label>
      <input value={name} onChange={e=>setName(e.target.value)}
        style={{ width:"100%", padding:"8px 12px", background:T.bg2, border:`1px solid ${T.border2}`, borderRadius:8, color:T.text1, fontSize:13, outline:"none", boxSizing:"border-box", marginBottom:10 }}/>
      <div style={{ display:"flex", gap:8 }}>
        <Btn variant="primary" size="sm" onClick={async()=>{ await onEdit(mr.id, name); setEdit(false); }}>Save</Btn>
        <Btn variant="ghost" size="sm" onClick={()=>setEdit(false)} style={{border:`1px solid ${T.border2}`}}>Cancel</Btn>
      </div>
    </div>
  );

  return (
    <div style={{ padding:"13px 16px", borderRadius:14, background:T.bg0, border:`1px solid ${T.border1}`, marginBottom:10, display:"flex", gap:12, alignItems:"center", transition:"border-color 0.2s" }}
      onMouseOver={e=>(e.currentTarget.style.borderColor=c+"55")} onMouseOut={e=>(e.currentTarget.style.borderColor=T.border1)}>
      {/* Icon */}
      <div style={{ width:42, height:42, borderRadius:11, background:c+"18", border:`1px solid ${c}33`, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        <MediaThumb mr={mr} token={token} color={c}/>
      </div>
      {/* Info */}
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:13, fontWeight:700, color:T.text1, marginBottom:3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{mr.name}</div>
        <div style={{ fontSize:11, color:T.text3 }}>
          {mr.originalName} · {fmtSize(mr.fileSize)} · <span style={{ color:c, fontWeight:600 }}>{mr.mimeType?.split("/")[1]?.toUpperCase()}</span>
        </div>
      </div>
      {/* Actions */}
      <div style={{ display:"flex", gap:6, flexShrink:0 }}>
        <button onClick={()=>setEdit(true)} title="Rename"
          style={{ background:"none", border:`1px solid ${T.border2}`, borderRadius:8, cursor:"pointer", color:T.text3, padding:"5px 9px", fontSize:13, transition:"all 0.15s" }}
          onMouseOver={e=>{e.currentTarget.style.borderColor=T.sky;e.currentTarget.style.color=T.sky;}}
          onMouseOut={e=>{e.currentTarget.style.borderColor=T.border2;e.currentTarget.style.color=T.text3;}}>✏️</button>
        <button onClick={async()=>{setDel(true);await onDelete(mr.id);}} disabled={del} title="Delete"
          style={{ background:"none", border:`1px solid ${T.border2}`, borderRadius:8, cursor:"pointer", color:T.text3, padding:"5px 9px", fontSize:13, transition:"all 0.15s" }}
          onMouseOver={e=>{e.currentTarget.style.borderColor=T.rose;e.currentTarget.style.color=T.rose;}}
          onMouseOut={e=>{e.currentTarget.style.borderColor=T.border2;e.currentTarget.style.color=T.text3;}}>{del?"...":"🗑️"}</button>
      </div>
    </div>
  );
};

// ── Main page ──────────────────────────────────────────────────────
export default function KnowledgePage({ token, showToast, isOwner = true }) {
  const [knowledge,         setKnowledge]         = useState("");
  const [txtFile,           setTxtFile]           = useState(null);
  const [saving,            setSaving]            = useState(false);
  const [uploading,         setUploading]         = useState(false);
  const [loading,           setLoading]           = useState(true);
  const [mediaReplies,      setMediaReplies]      = useState([]);
  const [showForm,          setShowForm]          = useState(false);
  const [mrFiles,           setMrFiles]           = useState([]);
  const [mrAdding,          setMrAdding]          = useState(false);
  const [pipelines,         setPipelines]         = useState([]);
  const [selectedPipeline,  setSelectedPipeline]  = useState(null); // null = global (owner KB)
  const [activePipelineName, setActivePipelineName] = useState("");
  // AI Behaviour settings
  const [aiSettings,   setAiSettings]   = useState({
    aiModel: "gpt-4.1-mini", aiTemperature: 0.4, aiMaxTokens: 800, aiHistoryLimit: 50, aiPersona: "", aiLanguage: "",
  });
  const [aiSaving,     setAiSaving]     = useState(false);
  const txtRef   = useRef(null);
  const mediaRef = useRef(null);

  const kbUrl = (base) => selectedPipeline ? `${base}?pipelineId=${selectedPipeline}` : base;

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [kd, md, sd, plData] = await Promise.all([
        apiFetch(kbUrl("/api/knowledge"),{},token),
        apiFetch("/api/media-replies",{},token),
        apiFetch("/api/bot-settings",{},token),
        apiFetch("/api/pipelines",{},token),
      ]);
      setKnowledge(kd.knowledge||"");
      if (kd.pipelineName) setActivePipelineName(kd.pipelineName);
      if (kd.pipelineId && !selectedPipeline) setSelectedPipeline(String(kd.pipelineId));
      setMediaReplies(md.mediaReplies||[]);
      setPipelines(plData.pipelines||[]);
      if (sd.settings) {
        setAiSettings({
          aiModel:        sd.settings.aiModel        || "gpt-4.1-mini",
          aiTemperature:  sd.settings.aiTemperature  ?? 0.4,
          aiMaxTokens:    sd.settings.aiMaxTokens    ?? 800,
          aiHistoryLimit: sd.settings.aiHistoryLimit ?? 50,
          aiPersona:      sd.settings.aiPersona      || "",
          aiLanguage:     sd.settings.aiLanguage     || "",
        });
      }
    } catch { showToast?.("Failed to load","error"); }
    setLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, showToast, selectedPipeline]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const loadKB = useCallback(async () => {
    try {
      const kd = await apiFetch(kbUrl("/api/knowledge"),{},token);
      setKnowledge(kd.knowledge||"");
      if (kd.pipelineName) setActivePipelineName(kd.pipelineName);
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedPipeline]);

  const handlePipelineChange = (pid) => {
    setSelectedPipeline(pid || null);
  };
  useEffect(() => { if (!loading) loadKB(); }, [selectedPipeline]); // eslint-disable-line

  const saveKnowledge = async () => {
    setSaving(true);
    try {
      await apiFetch("/api/set-knowledge",{method:"POST",body:JSON.stringify({text:knowledge, pipelineId:selectedPipeline||undefined})},token);
      showToast?.("Knowledge base saved ✓");
    } catch { showToast?.("Failed to save","error"); }
    setSaving(false);
  };

  const uploadTxt = async () => {
    if (!txtFile) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", txtFile);
      const res = await fetch(`${API}${kbUrl("/api/upload-knowledge")}`,{method:"POST",headers:{Authorization:token},body:fd});
      if (res.ok) {
        const d = await apiFetch(kbUrl("/api/knowledge"),{},token);
        setKnowledge(d.knowledge||""); setTxtFile(null);
        showToast?.("File uploaded ✓");
      } else showToast?.("Upload failed","error");
    } catch { showToast?.("Network error","error"); }
    setUploading(false);
  };

  const saveAiSettings = async () => {
    setAiSaving(true);
    try {
      await apiFetch("/api/bot-settings",{method:"POST",body:JSON.stringify(aiSettings)},token);
      showToast?.("AI settings saved ✓");
    } catch { showToast?.("Failed to save","error"); }
    setAiSaving(false);
  };

  const addMedia = async () => {
    if (!mrFiles.length) return showToast?.("Select one or more files","error");
    if (mrFiles.some(item=>!item.name.trim())) return showToast?.("Every file needs a name","error");
    setMrAdding(true);
    let uploaded = 0;
    const added = [];
    try {
      for (const item of mrFiles) {
        const fd = new FormData();
        fd.append("file", item.file);
        fd.append("name", item.name.trim());
        fd.append("caption", "");
        const res = await fetch(`${API}/api/media-replies`,{method:"POST",headers:{Authorization:token},body:fd});
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.error || `Failed to upload ${item.file.name}`);
        added.push(data.mediaReply);
        uploaded++;
      }
      setMediaReplies(p=>[...p,...added]);
      setMrFiles([]); setShowForm(false);
      if (mediaRef.current) mediaRef.current.value = "";
      showToast?.(`${uploaded} file${uploaded===1?"":"s"} uploaded ✓`);
    } catch (err) {
      if (added.length) setMediaReplies(p=>[...p,...added]);
      if (uploaded) setMrFiles(files=>files.slice(uploaded));
      showToast?.(`${err.message}${uploaded?` (${uploaded} uploaded successfully)`:""}`,"error");
    }
    setMrAdding(false);
  };

  const deleteMedia = async (id) => {
    try {
      await apiFetch(`/api/media-replies/${id}`,{method:"DELETE"},token);
      setMediaReplies(p=>p.filter(m=>m.id!==id));
      showToast?.("Deleted ✓");
    } catch { showToast?.("Failed","error"); }
  };

  const renameMedia = async (id, newName) => {
    try {
      await apiFetch(`/api/media-replies/${id}`,{method:"PATCH",body:JSON.stringify({name:newName})},token);
      setMediaReplies(p=>p.map(m=>m.id===id?{...m,name:newName}:m));
      showToast?.("Renamed ✓");
    } catch { showToast?.("Failed","error"); }
  };

  const inp = { width:"100%", padding:"9px 12px", background:T.bg0, border:`1px solid ${T.border1}`, borderRadius:9, color:T.text1, fontSize:13, outline:"none", boxSizing:"border-box", transition:"border-color 0.2s" };

  if (loading) return (
    <div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center"}}>
      <div style={{width:26,height:26,border:`2px solid ${T.border2}`,borderTopColor:T.emerald,borderRadius:"50%",animation:"spin 0.7s linear infinite"}}/>
    </div>
  );

  return (
    <div style={{ flex:1, overflowY:"auto", padding:"26px 28px 50px" }}>

      {/* ── Knowledge Base ─────────────────────────────────────── */}
      <div style={{ marginBottom:12 }}>
        <div style={{ fontSize:16, fontWeight:700, color:T.text1 }}>AI Knowledge Base</div>
        <div style={{ fontSize:13, color:T.text3, marginTop:3 }}>Everything written here is read by the AI to answer customer questions</div>
      </div>

      {/* Pipeline selector (owners) or info badge (agents) */}
      {isOwner && pipelines.length > 0 && (
        <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
          <span style={{ fontSize:12, fontWeight:600, color:T.text3, whiteSpace:"nowrap" }}>Pipeline KB:</span>
          <select
            value={selectedPipeline || ""}
            onChange={e => handlePipelineChange(e.target.value)}
            style={{ padding:"7px 12px", background:T.bg1, border:`1px solid ${T.border2}`, borderRadius:9, color:T.text1, fontSize:13, outline:"none", cursor:"pointer" }}
          >
            <option value="">Global (owner default)</option>
            {pipelines.map(p => (
              <option key={p._id} value={p._id}>{p.name}</option>
            ))}
          </select>
          {selectedPipeline && (
            <span style={{ fontSize:11, padding:"3px 9px", borderRadius:7, background:"rgba(99,102,241,0.12)", color:"#818cf8", fontWeight:600 }}>
              Editing: {pipelines.find(p => String(p._id) === selectedPipeline)?.name || "Pipeline KB"}
            </span>
          )}
        </div>
      )}
      {!isOwner && activePipelineName && (
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:16, padding:"8px 14px", borderRadius:10, background:"rgba(99,102,241,0.08)", border:"1px solid rgba(99,102,241,0.2)", width:"fit-content" }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          <span style={{ fontSize:12, color:"#818cf8", fontWeight:600 }}>Knowledge Base for: {activePipelineName}</span>
        </div>
      )}

      <Card style={{ marginBottom:24 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
          <div>
            <div style={{ fontSize:13, fontWeight:600, color:T.text1 }}>Business Information</div>
            <div style={{ fontSize:12, color:T.text3, marginTop:2 }}>Write like a FAQ — the more you add, the smarter the AI answers</div>
          </div>
          <span style={{ fontSize:11, color:T.text3, fontFamily:base.mono, background:T.bg0, padding:"3px 10px", borderRadius:8, border:`1px solid ${T.border1}`, flexShrink:0 }}>
            {knowledge.length.toLocaleString()} chars
          </span>
        </div>

        <textarea value={knowledge} onChange={e=>setKnowledge(e.target.value)}
          placeholder={"Business: Ahmed's Clothing Store\nHours: Mon–Sat 10am–9pm\nLocation: Lahore, Liberty Market\n\nProducts:\n- Men's Kurtas (PKR 800–3500)\n- Women's Suits (PKR 1200–5000)\n\nMenu / Price List: See attached menu image\n\nFAQs:\nQ: Do you deliver?\nA: Yes, within Lahore for Rs.100\n\nQ: What's in the menu?\nA: We have burgers, pizza, pasta. Prices from Rs.300–800."}
          style={{ width:"100%", minHeight:260, padding:"13px 15px", background:T.bg0, border:`1px solid ${T.border1}`, borderRadius:10, color:T.text1, fontSize:13, outline:"none", resize:"vertical", fontFamily:base.mono, lineHeight:1.75, boxSizing:"border-box", transition:"border-color 0.2s" }}
          onFocus={e=>(e.target.style.borderColor=T.emerald)} onBlur={e=>(e.target.style.borderColor=T.border1)} />

        <div style={{ display:"flex", alignItems:"center", gap:12, marginTop:14, flexWrap:"wrap" }}>
          <Btn variant="primary" onClick={saveKnowledge} disabled={saving}>
            {saving ? "Saving..." : <><Icon name="upload" size={13} color="#fff"/> Save Knowledge</>}
          </Btn>
          <button onClick={()=>txtRef.current?.click()}
            style={{ background:"none", border:`1px solid ${T.border2}`, borderRadius:8, cursor:"pointer", color:T.text3, padding:"7px 12px", fontSize:12, fontFamily:base.font, transition:"all 0.15s" }}
            onMouseOver={e=>{e.currentTarget.style.color=T.sky;e.currentTarget.style.borderColor=T.sky;}}
            onMouseOut={e=>{e.currentTarget.style.color=T.text3;e.currentTarget.style.borderColor=T.border2;}}>
            📄 {txtFile ? txtFile.name : "Import .txt file"}
          </button>
          <input ref={txtRef} type="file" accept=".txt,.text" style={{display:"none"}} onChange={e=>setTxtFile(e.target.files[0])}/>
          {txtFile && <>
            <Btn variant="primary" size="sm" onClick={uploadTxt} disabled={uploading}>{uploading?"Importing...":"Apply"}</Btn>
            <Btn variant="ghost"   size="sm" onClick={()=>setTxtFile(null)} style={{border:`1px solid ${T.border2}`}}>✕</Btn>
          </>}
          {!knowledge && <span style={{ fontSize:12, color:T.amber, marginLeft:"auto", display:"flex", alignItems:"center", gap:5 }}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={T.amber} strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg> Empty — AI gives generic answers</span>}
        </div>
      </Card>

      {/* ── AI Behaviour ──────────────────────────────────────────── */}
      <div style={{ marginBottom:8 }}>
        <div style={{ fontSize:16, fontWeight:700, color:T.text1 }}>AI Behaviour</div>
        <div style={{ fontSize:13, color:T.text3, marginTop:3 }}>Control how the AI thinks, replies, and remembers conversations</div>
      </div>

      <Card style={{ marginBottom:24 }}>
        <div className="grid-2" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20, marginBottom:20 }}>

          {/* Temperature */}
          <div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
              <div>
                <div style={{ fontSize:13, fontWeight:600, color:T.text1 }}>Creativity / Temperature</div>
                <div style={{ fontSize:11, color:T.text3, marginTop:2 }}>Low = strict & factual · High = creative & varied</div>
              </div>
              <span style={{ fontFamily:base.mono, fontSize:14, fontWeight:700, color:T.emerald, background:T.emeraldDim, padding:"3px 10px", borderRadius:8, border:"1px solid rgba(99,102,241,0.2)" }}>
                {aiSettings.aiTemperature.toFixed(1)}
              </span>
            </div>
            <div style={{ position:"relative", height:6, marginBottom:6 }}>
              <div style={{ position:"absolute", inset:0, borderRadius:3, background:T.border2 }}/>
              <div style={{ position:"absolute", top:0, left:0, height:6, borderRadius:3, background:`linear-gradient(90deg,${T.emerald},${T.sky})`, width:`${aiSettings.aiTemperature*100}%`, transition:"width 0.15s" }}/>
              <input type="range" min="0" max="1" step="0.1" value={aiSettings.aiTemperature}
                onChange={e=>setAiSettings(p=>({...p,aiTemperature:parseFloat(e.target.value)}))}
                style={{ position:"absolute", top:-4, left:0, width:"100%", height:14, opacity:0, cursor:"pointer" }}/>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:T.text3 }}>
              <span>0.0 — Very strict</span><span>1.0 — Very creative</span>
            </div>
            <div style={{ marginTop:8, padding:"8px 10px", borderRadius:8, background:
              aiSettings.aiTemperature <= 0.3 ? T.skyDim :
              aiSettings.aiTemperature <= 0.6 ? T.emeraldDim : T.amberDim,
              fontSize:11,
              color: aiSettings.aiTemperature <= 0.3 ? T.sky :
                     aiSettings.aiTemperature <= 0.6 ? T.emerald : T.amber }}>
              {aiSettings.aiTemperature <= 0.3 ? "📌 Very factual — AI sticks strictly to knowledge base only" :
               aiSettings.aiTemperature <= 0.6 ? "✅ Balanced — factual but natural sounding replies" :
               "🎨 Creative — varied replies but may go outside knowledge base"}
            </div>
          </div>

          {/* Max reply length */}
          <div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
              <div>
                <div style={{ fontSize:13, fontWeight:600, color:T.text1 }}>Max Reply Length</div>
                <div style={{ fontSize:11, color:T.text3, marginTop:2 }}>Max tokens in one reply (~0.75 words per token)</div>
              </div>
              <span style={{ fontFamily:base.mono, fontSize:14, fontWeight:700, color:T.sky, background:T.skyDim, padding:"3px 10px", borderRadius:8, border:"1px solid rgba(56,189,248,0.2)" }}>
                {aiSettings.aiMaxTokens}
              </span>
            </div>
            <div style={{ position:"relative", height:6, marginBottom:6 }}>
              <div style={{ position:"absolute", inset:0, borderRadius:3, background:T.border2 }}/>
              <div style={{ position:"absolute", top:0, left:0, height:6, borderRadius:3, background:`linear-gradient(90deg,${T.sky},${T.violet})`, width:`${((aiSettings.aiMaxTokens-200)/(2000-200))*100}%`, transition:"width 0.15s" }}/>
              <input type="range" min="200" max="2000" step="100" value={aiSettings.aiMaxTokens}
                onChange={e=>setAiSettings(p=>({...p,aiMaxTokens:parseInt(e.target.value)}))}
                style={{ position:"absolute", top:-4, left:0, width:"100%", height:14, opacity:0, cursor:"pointer" }}/>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:T.text3 }}>
              <span>200 — Very short</span><span>2000 — Very long</span>
            </div>
            <div style={{ marginTop:8, fontSize:11, color:T.text3 }}>
              ≈ {Math.round(aiSettings.aiMaxTokens * 0.75)} words · {aiSettings.aiMaxTokens <= 400 ? "Short replies only" : aiSettings.aiMaxTokens <= 800 ? "Normal replies" : "Detailed long replies"}
            </div>
          </div>
        </div>

        {/* Memory / History limit */}
        <div style={{ marginBottom:18 }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
            <div>
              <div style={{ fontSize:13, fontWeight:600, color:T.text1 }}>Conversation Memory</div>
              <div style={{ fontSize:11, color:T.text3, marginTop:2 }}>How many past messages AI remembers in each conversation</div>
            </div>
            <span style={{ fontFamily:base.mono, fontSize:14, fontWeight:700, color:T.violet, background:T.violetDim, padding:"3px 10px", borderRadius:8, border:"1px solid rgba(139,92,246,0.2)" }}>
              {aiSettings.aiHistoryLimit} msgs
            </span>
          </div>
          <div style={{ position:"relative", height:6, marginBottom:6 }}>
            <div style={{ position:"absolute", inset:0, borderRadius:3, background:T.border2 }}/>
            <div style={{ position:"absolute", top:0, left:0, height:6, borderRadius:3, background:`linear-gradient(90deg,${T.violet},${T.emerald})`, width:`${((aiSettings.aiHistoryLimit-10)/(100-10))*100}%`, transition:"width 0.15s" }}/>
            <input type="range" min="10" max="100" step="10" value={aiSettings.aiHistoryLimit}
              onChange={e=>setAiSettings(p=>({...p,aiHistoryLimit:parseInt(e.target.value)}))}
              style={{ position:"absolute", top:-4, left:0, width:"100%", height:14, opacity:0, cursor:"pointer" }}/>
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:T.text3 }}>
            <span>10 — Short memory</span><span>100 — Long memory</span>
          </div>
        </div>

        <div className="grid-2" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:18 }}>
          {/* AI Persona */}
          <div>
            <label style={{ display:"block", fontSize:11, color:T.text3, marginBottom:5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em" }}>
              AI Persona / Tone <span style={{ color:T.text3, fontWeight:400, textTransform:"none" }}>(optional)</span>
            </label>
            <textarea value={aiSettings.aiPersona}
              onChange={e=>setAiSettings(p=>({...p,aiPersona:e.target.value}))}
              placeholder={"You are Sofia, a friendly sales agent for Skycap. Be warm, professional, and encouraging. Always end with a follow-up question."}
              style={{ width:"100%", minHeight:90, padding:"9px 12px", background:T.bg0, border:`1px solid ${T.border1}`, borderRadius:9, color:T.text1, fontSize:12, outline:"none", resize:"vertical", fontFamily:base.font, lineHeight:1.6, boxSizing:"border-box", transition:"border-color 0.2s" }}
              onFocus={e=>(e.target.style.borderColor=T.emerald)} onBlur={e=>(e.target.style.borderColor=T.border1)}/>
            <div style={{ fontSize:11, color:T.text3, marginTop:4 }}>Define the bot's name, role, and personality</div>
          </div>

          {/* Language override */}
          <div>
            <label style={{ display:"block", fontSize:11, color:T.text3, marginBottom:5, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em" }}>
              Language Override <span style={{ color:T.text3, fontWeight:400, textTransform:"none" }}>(optional)</span>
            </label>
            <select value={aiSettings.aiLanguage}
              onChange={e=>setAiSettings(p=>({...p,aiLanguage:e.target.value}))}
              style={{ width:"100%", padding:"9px 12px", background:T.bg0, border:`1px solid ${T.border1}`, borderRadius:9, color:T.text1, fontSize:13, outline:"none", marginBottom:8 }}>
              <option value="">Auto-detect (recommended)</option>
              <option value="English">Always English</option>
              <option value="Urdu (Roman)">Always Roman Urdu</option>
              <option value="Urdu (Nastaliq script)">Always Urdu</option>
              <option value="Arabic">Always Arabic</option>
              <option value="Hindi">Always Hindi</option>
              <option value="French">Always French</option>
            </select>
            <div style={{ fontSize:11, color:T.text3 }}>Force a specific language or let AI match the customer</div>
          </div>
        </div>

        <Btn variant="primary" onClick={saveAiSettings} disabled={aiSaving}>
          {aiSaving
            ? <><div style={{width:13,height:13,border:"2px solid rgba(0,0,0,0.2)",borderTopColor:"#fff",borderRadius:"50%",animation:"spin 0.7s linear infinite"}}/> Saving...</>
            : <>🧠 Save AI Settings</>}
        </Btn>
      </Card>

      {/* ── Media Files ────────────────────────────────────────── */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
        <div>
          <div style={{ fontSize:16, fontWeight:700, color:T.text1 }}>Media Files</div>
          <div style={{ fontSize:13, color:T.text3, marginTop:3 }}>Upload images, PDFs or videos — the AI sends them automatically based on your knowledge base</div>
        </div>
        <Btn variant="primary" onClick={()=>setShowForm(v=>!v)}>{showForm?"✕ Cancel":"+ Upload Files"}</Btn>
      </div>

      {/* How it works */}
      <div style={{ padding:"12px 16px", borderRadius:12, background:T.skyDim, border:`1px solid rgba(56,189,248,0.2)`, marginBottom:20 }}>
        <div style={{ display:"flex", gap:10 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={T.sky} strokeWidth="1.8" style={{flexShrink:0,marginTop:1}}><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
          <div style={{ fontSize:12, color:T.sky, lineHeight:1.75 }}>
            <strong>How it works:</strong> Upload your menu image and name it <em>"Restaurant Menu"</em>. In your knowledge base write <em>"Menu: See our menu image for full list of dishes and prices"</em>. Now when a customer says <em>"send menu"</em> or <em>"show me the menu"</em> — the AI sends the image. If they ask <em>"what's in the menu?"</em> — AI answers from your knowledge base instead.
          </div>
        </div>
      </div>

      {/* Bulk upload form */}
      {showForm && (
        <Card style={{ marginBottom:20, border:`1px solid rgba(99,102,241,0.3)`, background:"rgba(99,102,241,0.03)" }}>
          <div style={{ fontSize:14, fontWeight:700, color:T.indigo, marginBottom:18 }}>Upload Media Files</div>

          <div style={{ marginBottom:16 }}>
            <label style={{ display:"block", fontSize:11, color:T.text3, marginBottom:6, fontWeight:600, textTransform:"uppercase", letterSpacing:"0.05em" }}>Files * (max 16MB each)</label>
            <div onClick={()=>mediaRef.current?.click()}
              style={{ border:`2px dashed ${mrFiles.length?T.emerald:T.border2}`, borderRadius:12, padding:"24px 16px", textAlign:"center", cursor:"pointer", background:mrFiles.length?"rgba(99,102,241,0.05)":"transparent" }}>
              {mrFiles.length ? (
                <div>
                  <div style={{ fontSize:14, color:T.emerald, fontWeight:700 }}>{mrFiles.length} file{mrFiles.length===1?"":"s"} selected</div>
                  <div style={{ fontSize:11, color:T.text3, marginTop:4 }}>Click to choose again</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize:14, color:T.text2, fontWeight:600 }}>Click to select multiple files</div>
                  <div style={{ fontSize:11, color:T.text3, marginTop:5 }}>JPG · PNG · WEBP · GIF · PDF · MP4 · max 16MB</div>
                </div>
              )}
              <input ref={mediaRef} type="file" multiple
                accept="image/jpeg,image/png,image/webp,image/gif,application/pdf,video/mp4,video/3gpp"
                style={{display:"none"}} onChange={e=>setMrFiles(Array.from(e.target.files||[]).map(file=>({file,name:file.name.replace(/\.[^.]+$/,"")})))}/>
            </div>

            {mrFiles.length>0 && (
              <div style={{display:"grid",gap:8,marginTop:12}}>
                {mrFiles.map((item,index)=>(
                  <div key={`${item.file.name}-${item.file.lastModified}`} style={{display:"flex",alignItems:"center",gap:10,padding:9,border:`1px solid ${T.border1}`,borderRadius:10,background:T.bg0}}>
                    <div style={{width:44,height:44,borderRadius:8,overflow:"hidden",display:"flex",alignItems:"center",justifyContent:"center",background:fileColor(item.file.type)+"18",flexShrink:0}}>
                      {item.file.type.startsWith("image/") ? <LocalImagePreview file={item.file}/> : fileIcon(item.file.type,fileColor(item.file.type))}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <input value={item.name} onChange={e=>setMrFiles(files=>files.map((entry,i)=>i===index?{...entry,name:e.target.value}:entry))} placeholder="Media name" style={{...inp,padding:"7px 10px"}}/>
                      <div style={{fontSize:10,color:T.text3,marginTop:3}}>{item.file.name} · {fmtSize(item.file.size)}</div>
                    </div>
                    <button type="button" onClick={()=>setMrFiles(files=>files.filter((_,i)=>i!==index))} title="Remove" style={{background:"none",border:"none",color:T.rose,cursor:"pointer",fontSize:18}}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div style={{ display:"flex", gap:10 }}>
            <Btn variant="primary" onClick={addMedia} disabled={mrAdding||!mrFiles.length||mrFiles.some(item=>!item.name.trim())}>
              {mrAdding
                ? <><div style={{width:13,height:13,border:"2px solid rgba(0,0,0,0.2)",borderTopColor:"#fff",borderRadius:"50%",animation:"spin 0.7s linear infinite"}}/> Uploading...</>
                : <><span>📤</span> Upload {mrFiles.length||""} File{mrFiles.length===1?"":"s"}</>}
            </Btn>
            <Btn variant="ghost" onClick={()=>{setShowForm(false);setMrFiles([]);if(mediaRef.current)mediaRef.current.value="";}} style={{border:`1px solid ${T.border2}`}}>Cancel</Btn>
          </div>
        </Card>
      )}

      {/* Files list */}
      {mediaReplies.length===0 && !showForm ? (
        <Card style={{ textAlign:"center", padding:"36px 24px", marginBottom:20 }}>
          <div style={{ display:"flex", justifyContent:"center", marginBottom:12, opacity:0.2 }}><svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.2"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg></div>
          <div style={{ fontSize:14, fontWeight:600, color:T.text2, marginBottom:6 }}>No media files yet</div>
          <div style={{ fontSize:13, color:T.text3, marginBottom:18, lineHeight:1.65 }}>
            Upload your menu image, price list PDF, or any file<br/>the bot should send to customers automatically
          </div>
          <Btn variant="primary" onClick={()=>setShowForm(true)}>+ Upload first file</Btn>
        </Card>
      ) : (
        <div style={{ marginBottom:20 }}>
          {mediaReplies.map(mr=>(
            <MediaCard key={mr.id} mr={mr} token={token} onDelete={deleteMedia} onEdit={renameMedia}/>
          ))}
        </div>
      )}

      {/* Tips */}
      <div className="grid-2" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 }}>
        <Card>
          <div style={{ fontSize:13, fontWeight:600, color:T.text1, marginBottom:12, display:"flex", alignItems:"center", gap:7 }}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.text3} strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg> Auto Language Detection</div>
          {[["اردو","Urdu"],["English","English"],["عربي","Arabic"],["हिंदी","Hindi"]].map(([s,l],i)=>(
            <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"8px 12px", borderRadius:8, background:T.bg0, border:`1px solid ${T.border1}`, marginBottom:6, fontSize:13 }}>
              <span style={{ color:T.text2 }}>{s}</span>
              <span style={{ color:T.emerald, fontWeight:600 }}>→ AI replies in {l}</span>
            </div>
          ))}
        </Card>
        <Card>
          <div style={{ fontSize:13, fontWeight:600, color:T.text1, marginBottom:12, display:"flex", alignItems:"center", gap:7 }}><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={T.indigo} strokeWidth="2.2"><polyline points="20 6 9 17 4 12"/></svg> How to get best results</div>
          {[
            ["Name files clearly",   "\"Restaurant Menu\" not \"IMG_001\""],
            ["Mention files in KB",  "Write in knowledge base: \"Menu: See menu image\""],
            ["Describe file content","Add dish names, prices in KB so AI can answer questions"],
            ["Both text + image",    "AI can send image AND answer questions about it"],
          ].map(([t,d],i)=>(
            <div key={i} style={{ display:"flex", gap:10, padding:"8px 0", borderBottom:i<3?`1px solid ${T.border1}`:"none" }}>
              <div style={{ width:6, height:6, borderRadius:"50%", background:T.emerald, marginTop:6, flexShrink:0 }}/>
              <div>
                <div style={{ fontSize:13, fontWeight:500, color:T.text1 }}>{t}</div>
                <div style={{ fontSize:12, color:T.text3, marginTop:2 }}>{d}</div>
              </div>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}
